import api from './api';

export interface UserApiPermissions {
  user_id: string;
  user_email: string;
  groups: string[];
  endpoints: Array<{
    endpoint_id: number;
    path: string;
    method: string;
    name: string;
    category?: string;
    can_read: boolean;
    can_write: boolean;
  }>;
  total_endpoints: number;
}

// Shared by every caller of getUserApiPermissions; see that method for why.
const USER_API_PERMISSIONS_TTL_MS = 5 * 60 * 1000;
let userApiPermissions: { at: number; request: Promise<UserApiPermissions> } | null = null;

export interface AzureADGroup {
  id: number;
  group_id: string;
  group_name: string;
  display_name?: string;
  description?: string;
  enabled_for_permissions: boolean;
  sort_order: number;
}

export interface PagePermission {
  id: number;
  group_id: number;
  route_path: string;
  route_name: string;
  module_name?: string;
}

export interface RouteDefinition {
  path: string;
  name: string;
  module: string;
}

export interface UserPermissions {
  user_id: string;
  user_email: string;
  groups: string[];  // Group IDs
  accessible_routes: string[];  // Route paths
  job_title?: string;
  department?: string;
  home_region?: string;
  home_region_as_default?: boolean;
  default_yard?: number;
  v1_username?: string;
}

export interface AzureADUser {
  id: number;
  user_id: string;
  display_name: string;
  email: string;
  user_principal_name?: string;
  job_title?: string;
  department?: string;
  office_location?: string;
  v1_username?: string;
  home_region?: string;
  home_region_as_default?: boolean;
  default_yard?: number;
  is_lc?: boolean;
  enabled_for_permissions: boolean;
  hidden_from_permissions: boolean;
  last_synced?: string;
}

/** Most recent admin force sign-out for a user; all null if never signed out. */
export interface ForceLogoutStatus {
  user_key: string;
  /** UTC epoch seconds — tokens issued before this are rejected. */
  force_logout_after: number | null;
  requested_by: string | null;
  reason: string | null;
  created_at: string | null;
  /**
   * Epoch when the kick was actually delivered to the client, or null while still
   * pending. A mark fires once and is then consumed — see the deliver-once fix in
   * migration 348.
   */
  delivered_at: number | null;
}

export interface ForceLogoutResult {
  message: string;
  user_key: string;
  force_logout_after: number;
  requested_by: string;
  reason: string | null;
}

export interface V1User {
  first?: string;
  last?: string;
  username: string;
  email?: string;
}

export interface AccessLevel {
  id: number;
  name: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
}

// API Endpoint Permission Types
export interface ApiEndpoint {
  id: number;
  path: string;
  method: string;
  name: string;
  category?: string;
  subcategory?: string;
  description?: string;
  requires_auth: boolean;
  is_active?: boolean;
}

export interface ApiEndpointPermission {
  id: number;
  group_id: number;
  endpoint_id: number;
  can_read: boolean;
  can_write: boolean;
  granted_by?: string;
  granted_at?: string;
  endpoint?: ApiEndpoint;
}

export interface ApiEndpointPermissionInput {
  endpoint_id: number;
  can_read: boolean;
  can_write: boolean;
}

export interface ApiEndpointsByModule {
  modules: Record<string, ApiEndpoint[]>;
  total: number;
}

export interface ApiEndpointPageAssignment {
  id: number;
  endpoint_id: number;
  page_route: string;
  required: boolean;
  assigned_by?: string;
  assigned_at?: string;
}

export interface PermissionPageEndpoint {
  endpoint_id: number;
  method: string;
  path: string;
  name: string;
  required: boolean;
  assignment_id: number;
  is_shared: boolean;
}

export interface PermissionPage {
  route_path: string;
  route_name: string;
  module: string;
  sort_order: number;
  endpoints: PermissionPageEndpoint[];
}

export interface PagePermissionInput {
  route_path: string;
  route_name: string;
  module_name?: string;
}

export interface GroupMember {
  id: string;
  displayName: string;
  mail?: string;
  userPrincipalName?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
}

class PermissionsService {
  /**
   * Get all available routes in the application
   */
  async getAvailableRoutes(signal?: AbortSignal): Promise<RouteDefinition[]> {
    const response = await api.get<RouteDefinition[]>('/permissions/routes', { signal });
    return response.data;
  }

  /**
   * Get all Azure AD groups
   */
  async getAllGroups(signal?: AbortSignal): Promise<AzureADGroup[]> {
    const response = await api.get<AzureADGroup[]>('/permissions/groups', { signal });
    return response.data;
  }

  /**
   * Get only groups enabled for permissions
   */
  async getEnabledGroups(signal?: AbortSignal): Promise<AzureADGroup[]> {
    const response = await api.get<AzureADGroup[]>('/permissions/groups/enabled', { signal });
    return response.data;
  }

  /**
   * Enable or disable a group for permission management
   */
  async enableGroup(groupId: number, enabled: boolean): Promise<{ message: string; group: AzureADGroup }> {
    const response = await api.put<{ message: string; group: AzureADGroup }>(
      `/permissions/groups/${groupId}/enable`,
      { enabled }
    );
    return response.data;
  }

  /**
   * Get all page permissions for a specific group
   */
  async getGroupPermissions(groupId: number): Promise<PagePermission[]> {
    const response = await api.get<PagePermission[]>(`/permissions/groups/${groupId}/permissions`);
    return response.data;
  }

  /**
   * Get all Azure AD members of a specific group
   */
  async getGroupMembers(groupId: number): Promise<GroupMember[]> {
    const response = await api.get<GroupMember[]>(`/permissions/groups/${groupId}/members`);
    return response.data;
  }

  /**
   * Assign page permissions to a group (replaces all existing permissions)
   */
  async assignPagePermissions(
    groupId: number,
    permissions: PagePermissionInput[]
  ): Promise<{ message: string; count: number }> {
    const response = await api.post<{ message: string; count: number }>(
      `/permissions/groups/${groupId}/permissions`,
      permissions
    );
    return response.data;
  }

  /**
   * Sync current user's group memberships and profile from Azure AD
   */
  async syncUserPermissions(): Promise<{ message: string; user_id: string; groups_count: number }> {
    const response = await api.post<{ message: string; user_id: string; groups_count: number }>(
      '/permissions/user/sync'
    );
    return response.data;
  }

  /**
   * Get current user's permissions including groups and accessible routes
   */
  async getUserPermissions(signal?: AbortSignal): Promise<UserPermissions> {
    const response = await api.get<UserPermissions>('/permissions/user/permissions', { signal });
    return response.data;
  }

  /**
   * Get current user's group memberships from Microsoft Graph
   */
  async getUserGroups(): Promise<{
    groups: Array<{ id: string; displayName: string; description?: string; mail?: string }>;
    profile: any;
  }> {
    const response = await api.get('/permissions/user/groups');
    return response.data;
  }

  /**
   * Check if user has access to a specific route
   */
  async hasRouteAccess(routePath: string, userPermissions: UserPermissions): Promise<boolean> {
    return userPermissions.accessible_routes.includes(routePath);
  }

  /**
   * Check if user is in any of the specified groups
   */
  async isInGroups(groupIds: string[], userPermissions: UserPermissions): Promise<boolean> {
    return groupIds.some(groupId => userPermissions.groups.includes(groupId));
  }

  /**
   * Get all available access levels
   */
  async getAccessLevels(signal?: AbortSignal): Promise<AccessLevel[]> {
    const response = await api.get<AccessLevel[]>('/permissions/access-levels', { signal });
    return response.data;
  }

  /**
   * Get current user's component access levels (highest-wins across groups)
   */
  async getUserComponentAccessLevels(signal?: AbortSignal): Promise<Record<string, number>> {
    const response = await api.get<Record<string, number>>('/permissions/components/access-levels', { signal });
    return response.data;
  }

  /**
   * Save component-level permissions to the backend (supports both groups and users).
   * `clear` names components whose LAST grant was removed: they have no row in the payload,
   * so the server would not otherwise know to delete their stored rows.
   */
  async saveComponentPermissions(permissions: Array<{
    component_id: string;
    component_name: string;
    entity_type: 'group' | 'user';
    group_id?: string;
    user_id?: string;
    access_level: number;
  }>, clear: string[] = []): Promise<{ message: string; count: number }> {
    const response = await api.post<{ message: string; count: number }>(
      '/permissions/components/permissions',
      permissions,
      clear.length ? { params: { clear: clear.join(',') } } : undefined,
    );
    return response.data;
  }

  /**
   * Get all component-level permissions from the backend (both groups and users)
   */
  async getComponentPermissions(signal?: AbortSignal): Promise<Array<{
    id: number;
    component_id: string;
    component_name: string;
    entity_type: 'group' | 'user';
    group_id?: string;
    user_id?: string;
    access_level: number;
  }>> {
    const response = await api.get('/permissions/components/permissions', { signal });
    return response.data;
  }

  // ============================================
  // AZURE AD USERS MANAGEMENT
  // ============================================

  /**
   * Get all Azure AD users
   */
  async getAllUsers(includeHidden: boolean = false): Promise<AzureADUser[]> {
    const response = await api.get<AzureADUser[]>('/permissions/users', {
      params: { include_hidden: includeHidden }
    });
    return response.data;
  }

  /**
   * Get only users enabled for permissions and not hidden
   */
  async getEnabledUsers(signal?: AbortSignal): Promise<AzureADUser[]> {
    const response = await api.get<AzureADUser[]>('/permissions/users/enabled', { signal });
    return response.data;
  }

  /**
   * Update user visibility in permissions dropdowns
   */
  async updateUserVisibility(userId: number, hidden: boolean): Promise<{ message: string; user: AzureADUser }> {
    const response = await api.put<{ message: string; user: AzureADUser }>(
      `/permissions/users/${userId}/visibility`,
      { hidden }
    );
    return response.data;
  }

  /**
   * Update user's v1 username
   */
  async updateUserV1Username(userId: number, v1Username: string | null): Promise<{ message: string; user: AzureADUser }> {
    const response = await api.put<{ message: string; user: AzureADUser }>(
      `/permissions/users/${userId}/v1-username`,
      { v1_username: v1Username }
    );
    return response.data;
  }

  /**
   * Search for V1 users from gwi.users table
   */
  async searchV1Users(query: string): Promise<V1User[]> {
    const response = await api.get<V1User[]>('/permissions/users/v1-users', {
      params: { query }
    });
    return response.data;
  }

  /**
   * Update user's home region
   */
  async updateUserHomeRegion(userId: number, homeRegion: string | null): Promise<{ message: string; user: AzureADUser }> {
    const response = await api.put<{ message: string; user: AzureADUser }>(
      `/permissions/users/${userId}/home-region`,
      { home_region: homeRegion }
    );
    return response.data;
  }

  /**
   * Update whether the user's home region auto-selects as the default in region filters
   */
  async updateUserHomeRegionAsDefault(userId: number, value: boolean): Promise<{ message: string; user: AzureADUser }> {
    const response = await api.put<{ message: string; user: AzureADUser }>(
      `/permissions/users/${userId}/home-region-as-default`,
      { home_region_as_default: value }
    );
    return response.data;
  }

  /**
   * Toggle whether a user is an LC — i.e. appears in the record-level LC assignment
   * dropdowns (order/quote/tablemaints via /cms-lookups/csrs).
   */
  async updateUserIsLc(userId: number, value: boolean): Promise<{ message: string; user: AzureADUser }> {
    const response = await api.put<{ message: string; user: AzureADUser }>(
      `/permissions/users/${userId}/is-lc`,
      { is_lc: value }
    );
    return response.data;
  }

  /**
   * Get available regions from cms.dispatchers
   */
  async getAvailableRegions(): Promise<string[]> {
    const response = await api.get<string[]>('/permissions/users/regions');
    return response.data;
  }

  /**
   * Update user's default yard (yard recno from locations table)
   */
  async updateUserDefaultYard(userId: number, defaultYard: number | null): Promise<{ message: string; user: AzureADUser }> {
    const response = await api.put<{ message: string; user: AzureADUser }>(
      `/permissions/users/${userId}/default-yard`,
      { default_yard: defaultYard }
    );
    return response.data;
  }

  /**
   * Force a user to sign out. Their existing tokens stop working within ~10s and
   * their client bounces them to the login screen.
   *
   * One-shot: they may sign straight back in (usually silently via SSO), which is
   * the point — this refreshes stale permissions and resets wedged clients. It is
   * not an account disable and needs no follow-up to undo.
   */
  async forceUserLogout(userId: number, reason?: string): Promise<ForceLogoutResult> {
    const response = await api.post<ForceLogoutResult>(
      `/permissions/users/${userId}/force-logout`,
      { reason: reason || null }
    );
    return response.data;
  }

  /**
   * Most recent force sign-out for a user, or nulls if never signed out.
   */
  async getUserForceLogout(userId: number): Promise<ForceLogoutStatus> {
    const response = await api.get<ForceLogoutStatus>(
      `/permissions/users/${userId}/force-logout`
    );
    return response.data;
  }

  /**
   * Get all GWI360- groups available for assignment
   */
  async getGwi360Groups(): Promise<Array<{ id: string; displayName: string }>> {
    const response = await api.get('/permissions/users/gwi360-groups');
    return response.data;
  }

  /**
   * Add a user to an Azure AD group
   */
  async addUserToGroup(userId: string, groupId: string): Promise<{ message: string }> {
    const response = await api.post(`/permissions/users/${userId}/groups/${groupId}`);
    return response.data;
  }

  /**
   * Remove a user from an Azure AD group
   */
  async removeUserFromGroup(userId: string, groupId: string): Promise<{ message: string }> {
    const response = await api.delete(`/permissions/users/${userId}/groups/${groupId}`);
    return response.data;
  }

  /**
   * Search for users in Azure AD (typeahead)
   */
  async searchUsers(query: string, limit: number = 20): Promise<{ users: any[] }> {
    const response = await api.get('/permissions/users/search', {
      params: { query, limit }
    });
    return response.data;
  }

  // ============================================
  // API ENDPOINT PERMISSIONS
  // ============================================

  /**
   * Get all API endpoints registered in the system
   */
  async getApiEndpoints(): Promise<ApiEndpoint[]> {
    const response = await api.get<ApiEndpoint[]>('/permissions/api-endpoints');
    return response.data;
  }

  /**
   * Get all API endpoints grouped by module
   */
  async getApiEndpointsByModule(): Promise<ApiEndpointsByModule> {
    const response = await api.get<ApiEndpointsByModule>('/permissions/api-endpoints/by-module');
    return response.data;
  }

  /**
   * Rebuild API endpoints table from FastAPI routes
   */
  async rebuildApiEndpoints(): Promise<{ message: string; stats: { total: number; created: number; updated: number; skipped: number } }> {
    const response = await api.post('/permissions/api-endpoints/rebuild');
    return response.data;
  }

  /**
   * Get API endpoint permissions for a specific group
   */
  async getGroupApiPermissions(groupId: number): Promise<ApiEndpointPermission[]> {
    const response = await api.get<ApiEndpointPermission[]>(`/permissions/api-endpoints/groups/${groupId}`);
    return response.data;
  }

  /**
   * Set API endpoint permissions for a group
   */
  async setGroupApiPermissions(
    groupId: number,
    permissions: ApiEndpointPermissionInput[]
  ): Promise<{ message: string; group_id: number; count: number }> {
    const response = await api.post(`/permissions/api-endpoints/groups/${groupId}`, permissions);
    return response.data;
  }

  /**
   * Get current user's API endpoint permissions based on group memberships.
   *
   * Cached per session. The response carries EVERY endpoint the caller's groups
   * are granted -- ~2,500 rows and 400 KB for a broadly granted group -- while
   * callers only ever test one endpoint, so refetching it per component mount
   * (the Market Conditions pill did this on every modal open) is pure waste.
   * The 5 minute TTL matches the backend's own permission cache, so a grant
   * change takes effect on the same timescale either way.
   */
  async getUserApiPermissions(options: { force?: boolean } = {}): Promise<UserApiPermissions> {
    const fresh = userApiPermissions && Date.now() - userApiPermissions.at < USER_API_PERMISSIONS_TTL_MS;
    if (options.force || !fresh) {
      const request = api.get('/permissions/api-endpoints/user/permissions')
        .then(response => response.data as UserApiPermissions)
        // Never cache a failure: the next caller should retry.
        .catch(error => { userApiPermissions = null; throw error; });
      userApiPermissions = { at: Date.now(), request };
    }
    return userApiPermissions!.request;
  }

  /** Drop the cached copy, e.g. after grants change in this session. */
  clearUserApiPermissionsCache(): void {
    userApiPermissions = null;
  }

  /**
   * Get all unique categories from API endpoints
   */
  async getApiEndpointCategories(): Promise<string[]> {
    const response = await api.get<string[]>('/permissions/api-endpoints/categories');
    return response.data;
  }

  /**
   * Update an API endpoint's category or name
   */
  async updateApiEndpoint(
    endpointId: number,
    updates: { category?: string; name?: string }
  ): Promise<{ message: string; endpoint: ApiEndpoint }> {
    const params = new URLSearchParams();
    if (updates.category) params.append('category', updates.category);
    if (updates.name) params.append('name', updates.name);

    const response = await api.put(`/permissions/api-endpoints/${endpointId}?${params.toString()}`);
    return response.data;
  }

  /**
   * Bulk update API endpoint categories
   */
  async bulkUpdateApiEndpointCategories(
    updates: Array<{ endpoint_id: number; category: string }>
  ): Promise<{ message: string; count: number }> {
    const response = await api.put('/permissions/api-endpoints/bulk-category', updates);
    return response.data;
  }

  /**
   * Get list of endpoints that bypass permission checks
   */
  async getBypassedEndpoints(): Promise<{ bypassed_endpoints: string[]; count: number; description: string }> {
    const response = await api.get('/permissions/bypassed-endpoints');
    return response.data;
  }

  /**
   * Get all frontend pages (from the permission_pages table) with their
   * endpoint assignments pre-resolved server-side. Replaces the
   * historical PAGE_ENDPOINT_MAPPINGS array.
   */
  async getPermissionPages(signal?: AbortSignal): Promise<PermissionPage[]> {
    const response = await api.get<PermissionPage[]>('/permissions/pages', { signal });
    return response.data;
  }

  /**
   * Assign an API endpoint to a frontend page route.
   */
  async assignApiEndpointToPage(
    endpointId: number,
    pageRoute: string,
    required: boolean = false
  ): Promise<ApiEndpointPageAssignment> {
    const response = await api.post<ApiEndpointPageAssignment>(
      `/permissions/api-endpoints/${endpointId}/page-assignments`,
      { page_route: pageRoute, required }
    );
    return response.data;
  }

  /**
   * Remove a dynamic endpoint→page assignment.
   */
  async removeApiEndpointPageAssignment(assignmentId: number): Promise<void> {
    await api.delete(`/permissions/api-endpoints/page-assignments/${assignmentId}`);
  }
}

export default new PermissionsService();
